import React, { useState, useEffect } from "react";
import { Button, Flex, Input, message, Select } from "antd";
import {
  CheckCircleOutlined,
  CheckOutlined,
  CopyOutlined,
  ReloadOutlined,
  ExportOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";

const { Option } = Select;

const FieldVisibilityRulesBuilder: React.FC = () => {
  const [selectedMode, setSelectedMode] = useState<string>("");
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [visibilityRules, setVisibilityRules] = useState<
    Record<string, string>
  >({});
  const [jsonInput, setJsonInput] = useState<string>("");

  // 🔹 Whenever mode+value chosen, update JSON
  useEffect(() => {
    if (selectedMode && selectedValue) {
      setVisibilityRules((prev) => ({
        ...prev,
        [selectedMode]: selectedValue,
      }));
    }
  }, [selectedMode, selectedValue]);

  // 🔹 Sync JSON display when rules change
  useEffect(() => {
    if (Object.keys(visibilityRules).length === 0) {
      setJsonInput(""); // empty string when no rules
    } else {
      setJsonInput(JSON.stringify(visibilityRules, null, 2));
    }
  }, [visibilityRules]);

  // 🔹 Load pasted JSON into state
  const loadJson = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      if (typeof parsed === "object" && parsed !== null) {
        setVisibilityRules(parsed);
        message.success("JSON loaded!");
      } else {
        throw new Error("Invalid format");
      }
    } catch (err: any) {
      message.error("Invalid JSON: " + err.message);
    }
  };

  // 🔹 Beautify
  const beautifyJson = (json: string) => {
    try {
      return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
      message.error("Invalid JSON for beautify");
      return json;
    }
  };

  // 🔹 Minify
  const minifyJson = (json: string) => {
    try {
      return JSON.stringify(JSON.parse(json));
    } catch {
      message.error("Invalid JSON for minify");
      return json;
    }
  };

  // 🔹 Validate
  const validateJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      message.success("Valid JSON!");
      return JSON.stringify(parsed, null, 2);
    } catch {
      message.error("Invalid JSON format");
      return json;
    }
  };

  // 🔹 Copy JSON Query
  const updateQueryJsonInput = () => {
    try {
      const parseddata = JSON.parse(jsonInput);
      const stringified = JSON.stringify(parseddata);
      const query = `Update FieldInterfaceMapping SET VisibilityRules = '${stringified}' Where Id = ??`;
      navigator.clipboard
        .writeText(query)
        .then(() => message.success("JSON Query copied!"))
        .catch(() => message.error("Failed to copy query!"));
    } catch {
      message.error("Invalid JSON format");
    }
  };

  return (
    <div className="App">
      <h4>Field visibility rules JSON builder</h4>

      {/* JSON Textbox */}
      <Input.TextArea
        rows={10}
        placeholder="Paste JSON here..."
        value={jsonInput}
        onChange={(e) => setJsonInput(e.target.value)}
        style={{ resize: "none", width: "100%" }}
      />

      {/* Action Buttons */}
      <div style={{ marginTop: 20, marginBottom: 8 }}>
        <Flex wrap gap="small">
          <Button
            onClick={loadJson}
            color="primary"
            variant="solid"
            icon={<ReloadOutlined />}
          >
            Load JSON
          </Button>

          <Button
            onClick={() => {
              try {
                JSON.parse(jsonInput);
                navigator.clipboard.writeText(jsonInput);
                message.success("JSON copied!");
              } catch {
                message.error("Invalid JSON format");
              }
            }}
            color="pink"
            variant="solid"
            icon={<CopyOutlined />}
          >
            Copy JSON
          </Button>

          <Button
            onClick={() => setJsonInput(beautifyJson(jsonInput))}
            color="purple"
            variant="solid"
            icon={<ExportOutlined />}
          >
            Beautify
          </Button>

          <Button
            onClick={() => setJsonInput(minifyJson(jsonInput))}
            color="orange"
            variant="solid"
            icon={<MinusCircleOutlined />}
          >
            Minify
          </Button>

          <Button
            onClick={() => setJsonInput(validateJson(jsonInput))}
            color="danger"
            variant="solid"
            icon={<CheckCircleOutlined />}
          >
            Validate
          </Button>

          <Button
            onClick={updateQueryJsonInput}
            color="cyan"
            variant="solid"
            icon={<CheckOutlined />}
          >
            Copy JSON Query
          </Button>
        </Flex>
      </div>

      {/* Dropdown UI */}
      <h4>Set visibility rules</h4>
      <table className="table table-bordered" style={{ width: "100%" }}>
        <thead style={{ display: "none" }}>
          <tr>
            <th scope="col"></th>
            <th scope="col"></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Visibility Mode</td>
            <td>
              <Select
                value={selectedMode}
                onChange={setSelectedMode}
                style={{ width: "100%" }}
                placeholder="Select mode"
              >
                <Option value="">Select</Option>
                <Option value="create">Create</Option>
                <Option value="edit">Edit</Option>
                <Option value="all">All</Option>
              </Select>
              <span>
                <strong>Note:</strong> Please select an form mode for
                visibility: Create, Edit, or All.
              </span>
            </td>
          </tr>
          <tr>
            <td>Visibility Value</td>
            <td>
              <Select
                value={selectedValue}
                onChange={setSelectedValue}
                style={{ width: "100%" }}
                placeholder="Select value"
                disabled={!selectedMode}
              >
                <Option value="">Select</Option>
                <Option value="readonly">Readonly</Option>
                <Option value="hidden">Hidden</Option>
              </Select>
              <span>
                <strong>Note:</strong> Select Readonly / Hidden for chosen Mode
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default FieldVisibilityRulesBuilder;
